import { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { isOnline, addConnectionListeners, registerSync } from '../../utils/pwa';
import { toast } from '../Toast';
import logger from '../../utils/logger';

export default function OfflineIndicator() {
  const [online, setOnline] = useState(isOnline());
  const [syncing, setSyncing] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setShowMessage(true);
      handleSync();

      // Hide message after 3 seconds
      setTimeout(() => setShowMessage(false), 3000);
    };

    const handleOffline = () => {
      setOnline(false);
      setShowMessage(true);
    };

    const cleanup = addConnectionListeners(handleOnline, handleOffline);

    return cleanup;
  }, []);

  const handleSync = async () => {
    setSyncing(true);

    try {
      // Register background sync
      await registerSync('sync-orders');
      await registerSync('sync-inventory');

      // Also try immediate sync
      await fetch('/health', { method: 'GET' });

      setTimeout(() => {
        setSyncing(false);
      }, 2000);
    } catch (error) {
      logger.error('Offline sync failed', { error });
      toast.error('Sync failed. Will retry when online.');
      setSyncing(false);
    }
  };

  // Don't show anything if online and message hidden
  if (online && !showMessage) {
    return null;
  }

  return (
    <>
      {/* Offline Banner */}
      {!online && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-3 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <WifiOff className="w-5 h-5 animate-pulse" />
              <div>
                <p className="font-semibold">You&apos;re offline</p>
                <p className="text-sm text-red-100">
                  Some features may be limited. Your changes will be synced when you&apos;re back online.
                </p>
              </div>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Online Toast */}
      {online && showMessage && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[300px]">
            {syncing ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Wifi className="w-5 h-5" />
            )}
            <div>
              <p className="font-semibold">Back online</p>
              <p className="text-sm text-green-100">
                {syncing ? 'Syncing your changes...' : 'All systems operational'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Small indicator (always visible when offline) */}
      {!online && (
        <div className="fixed bottom-4 left-4 z-40">
          <div className="bg-red-500 text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            Offline Mode
          </div>
        </div>
      )}
    </>
  );
}
